import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Upload, Link as LinkIcon, Image as ImageIcon } from "lucide-react";
import {
  getLogoUploadUrl,
  uploadLogoToS3,
  updateCorporate,
} from "../../../../Services/adminCorporates";
import { useCorporate } from "../CorporateContext";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Label } from "../../../../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../ui/card";
import { Skeleton } from "../../../../ui/skeleton";

const ALLOWED_EXT = ["png", "jpg", "jpeg"];
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB cap

const hexRegex = /^#([0-9a-fA-F]{3}){1,2}$/;

const ColorField = ({ id, label, hint, value, onChange, error }) => (
  <div className="grid gap-1.5">
    <Label htmlFor={id}>{label}</Label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={hexRegex.test(value || "") ? value : "#000000"}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-12 cursor-pointer rounded-md border border-input bg-background p-1"
        aria-label={`${label} color picker`}
      />
      <Input
        id={id}
        placeholder="#0d5eaf"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="font-mono"
      />
    </div>
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    {error && <p className="text-xs text-destructive">{error}</p>}
  </div>
);

/**
 * Branding tab — logo display + presigned-PUT upload + color editors.
 *
 * Logo upload flow (per spec §6.5):
 *   1. POST /api/admin/enterprises/{id}/logo/upload-url → { upload_url, ... }
 *   2. PUT the file directly to upload_url with the right Content-Type
 *   3. Refetch corporate detail so the new presigned logo_url is reflected
 */
const Branding = () => {
  const { id } = useParams();
  const { corporate, loading, refetch } = useCorporate();
  const fileInputRef = useRef(null);

  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [colorErrors, setColorErrors] = useState({});
  const [logoUrlInput, setLogoUrlInput] = useState("");
  const [savingLogoUrl, setSavingLogoUrl] = useState(false);
  const [logoUrlError, setLogoUrlError] = useState(null);

  const [savingColors, setSavingColors] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [savedNotice, setSavedNotice] = useState(null);

  const [fontScale, setFontScale] = useState(1);
  const [savingFontScale, setSavingFontScale] = useState(false);
  const [fontScaleNotice, setFontScaleNotice] = useState(null);

  // Sync local state when the corporate loads / changes.
  useEffect(() => {
    if (corporate) {
      setPrimaryColor(corporate.primary_color || "");
      setSecondaryColor(corporate.secondary_color || "");
      setFontScale(
        typeof corporate.content_font_scale === "number"
          ? corporate.content_font_scale
          : 1
      );
    }
  }, [corporate]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setSavedNotice(null);

    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setUploadError("Logo must be a PNG, JPG, or JPEG file.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_BYTES) {
      setUploadError("Logo file is larger than 2 MB. Please trim and try again.");
      e.target.value = "";
      return;
    }

    setUploading(true);
    try {
      const presigned = await getLogoUploadUrl(id, ext);
      if (!presigned?.upload_url) {
        throw new Error("Server did not return an upload URL.");
      }
      await uploadLogoToS3(presigned.upload_url, file, ext);
      await refetch();
      setSavedNotice("Logo uploaded.");
    } catch (err) {
      setUploadError(
        err?.response?.data?.message ||
          err?.message ||
          "Logo upload failed. Please try again."
      );
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSaveLogoUrl = async () => {
    setLogoUrlError(null);
    setSavedNotice(null);
    const trimmed = logoUrlInput.trim();
    if (!trimmed) {
      setLogoUrlError("Paste a logo URL first.");
      return;
    }
    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
        setLogoUrlError("URL must start with http(s)://");
        return;
      }
    } catch (_) {
      setLogoUrlError("That doesn't look like a valid URL.");
      return;
    }
    setSavingLogoUrl(true);
    try {
      await updateCorporate(id, { logo_url: trimmed });
      await refetch();
      setLogoUrlInput("");
      setSavedNotice("Logo URL saved.");
    } catch (err) {
      setLogoUrlError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save the logo URL."
      );
    } finally {
      setSavingLogoUrl(false);
    }
  };

  const handleSaveFontScale = async () => {
    setFontScaleNotice(null);
    setSavingFontScale(true);
    try {
      await updateCorporate(id, { content_font_scale: fontScale });
      await refetch();
      setFontScaleNotice("Display size saved.");
    } catch (err) {
      setFontScaleNotice(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save display size."
      );
    } finally {
      setSavingFontScale(false);
    }
  };

  const handleSaveColors = async () => {
    const errs = {};
    if (primaryColor && !hexRegex.test(primaryColor)) {
      errs.primary = "Use a valid hex like #0d5eaf";
    }
    if (secondaryColor && !hexRegex.test(secondaryColor)) {
      errs.secondary = "Use a valid hex like #4377fc";
    }
    setColorErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSavingColors(true);
    setSavedNotice(null);
    try {
      await updateCorporate(id, {
        primary_color: primaryColor || null,
        secondary_color: secondaryColor || null,
      });
      await refetch();
      setSavedNotice("Brand colors saved.");
    } catch (err) {
      setUploadError(
        err?.response?.data?.message || err?.message || "Failed to save colors."
      );
    } finally {
      setSavingColors(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Logo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-start gap-4">
            <div className="h-24 w-44 flex items-center justify-center rounded-md border bg-muted/30">
              {corporate?.logo_url ? (
                <img
                  src={corporate.logo_url}
                  alt={corporate.display_name || corporate.name}
                  className="max-h-20 max-w-[160px] object-contain"
                />
              ) : (
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 space-y-2">
              <p className="text-sm text-muted-foreground">
                PNG / JPG. Max 2 MB. Recommended 280×80 or larger.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  {uploading ? "Uploading…" : corporate?.logo_url ? "Replace logo" : "Upload logo"}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
              {uploadError && (
                <p className="text-xs text-destructive" role="alert">
                  {uploadError}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              or
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="logo_url">Use a hosted logo URL</Label>
            <p className="text-sm text-muted-foreground">
              Paste a public https:// URL — e.g. a CDN or your marketing site.
              This skips the upload and points the activation page directly at
              your hosted asset.
            </p>
            <div className="flex gap-2">
              <Input
                id="logo_url"
                type="url"
                placeholder="https://cdn.example.com/logo.png"
                value={logoUrlInput}
                onChange={(e) => setLogoUrlInput(e.target.value)}
                disabled={savingLogoUrl}
              />
              <Button
                variant="outline"
                onClick={handleSaveLogoUrl}
                disabled={savingLogoUrl || !logoUrlInput.trim()}
              >
                <LinkIcon className="h-4 w-4 mr-1" />
                {savingLogoUrl ? "Saving…" : "Use URL"}
              </Button>
            </div>
            {logoUrlError && (
              <p className="text-xs text-destructive" role="alert">
                {logoUrlError}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Brand colors</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ColorField
              id="primary_color"
              label="Primary color"
              hint="Used for the activation page accent."
              value={primaryColor}
              onChange={setPrimaryColor}
              error={colorErrors.primary}
            />
            <ColorField
              id="secondary_color"
              label="Secondary color"
              hint="Used for buttons and highlights."
              value={secondaryColor}
              onChange={setSecondaryColor}
              error={colorErrors.secondary}
            />
          </div>
          <div className="flex items-center justify-end gap-3">
            {savedNotice && (
              <span className="text-sm text-emerald-600">{savedNotice}</span>
            )}
            <Button onClick={handleSaveColors} disabled={savingColors}>
              {savingColors ? "Saving…" : "Save colors"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Display</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="content_font_scale">
              Content font size ({Math.round(fontScale * 100)}%)
            </Label>
            <input
              id="content_font_scale"
              type="range"
              min={0.85}
              max={1.4}
              step={0.05}
              value={fontScale}
              onChange={(e) => setFontScale(Number(e.target.value))}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Scales every text element on the activation page (headlines,
              section copy, bullets). 100% is the default.
            </p>
            <div
              className="rounded-md border bg-muted/30 px-4 py-3 text-sm"
              style={{ fontSize: `${fontScale}rem`, lineHeight: 1.4 }}
            >
              Preview at the chosen scale.
            </div>
          </div>
          <div className="flex items-center justify-end gap-3">
            {fontScaleNotice && (
              <span className="text-sm text-emerald-600">
                {fontScaleNotice}
              </span>
            )}
            <Button onClick={handleSaveFontScale} disabled={savingFontScale}>
              {savingFontScale ? "Saving…" : "Save display size"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Branding;
